import React, { useRef, useEffect } from 'react';

interface Star {
    x: number;
    y: number;
    z: number;
    opacity: number;
}

export const Starfield: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = window.innerWidth;
        let height = window.innerHeight;
        let animationFrameId: number;

        const stars: Star[] = [];
        const numStars = 200;
        const speed = 2; // Speed of stars moving towards viewer

        // Initialize stars
        const initStars = () => {
            stars.length = 0;
            for (let i = 0; i < numStars; i++) {
                stars.push({
                    x: Math.random() * width - width / 2,
                    y: Math.random() * height - height / 2,
                    z: Math.random() * width, // depth
                    opacity: Math.random()
                });
            }
        };

        const resizeCanvas = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
            initStars();
        };

        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        const draw = () => {
            // Clear canvas with some transparency for trails? No, simple clear for crisp stars.
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, width, height);

            // Center origin
            const cx = width / 2;
            const cy = height / 2;

            stars.forEach((star) => {
                // Move star closer
                star.z -= speed;

                // Reset if it passes the viewer
                if (star.z <= 0) {
                    star.z = width;
                    star.x = Math.random() * width - width / 2;
                    star.y = Math.random() * height - height / 2;
                }

                // Project 3D position to 2D
                const x = (star.x / star.z) * width + cx;
                const y = (star.y / star.z) * height + cy;

                // Calculate size based on depth (closer = bigger)
                const size = (1 - star.z / width) * 2.5;
                const opacity = (1 - star.z / width);

                if (x >= 0 && x < width && y >= 0 && y < height) {
                    ctx.beginPath();
                    ctx.arc(x, y, size, 0, Math.PI * 2);

                    // "Simply white and have slight glow"
                    ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
                    ctx.shadowBlur = 4; // Slight glow
                    ctx.shadowColor = 'white';

                    ctx.fill();

                    // Reset shadow for performance if needed, but for "glow" we need it.
                    // Note: excessive shadowBlur can be expensive. 4 is usually okay.
                }
            });

            animationFrameId = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 -z-10 pointer-events-none"
            style={{ background: 'black' }}
        />
    );
};
